"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { scheduleModalPointerEventsCleanup } from "@/lib/ui/modal-pointer-events";
import { registerNativeModalBack } from "@/lib/ui/native-modal-back";
import { pinBody, unpinBody } from "@/lib/ui/pinned-body";
import { cn } from "@/lib/utils";

let rootScrollLockCount = 0;
let rootOverflow = "";
let rootOverscrollBehavior = "";
let bodyOverflow = "";
let bodyOverscrollBehavior = "";
let lastTouchY: number | null = null;
const DrawerDismissContext = React.createContext<(() => void) | null>(null);

function canDrawerScroll(target: EventTarget | null, deltaY: number) {
  if (!(target instanceof Element)) return false;
  const drawer = target.closest('[data-slot="drawer-content"]');
  if (!drawer) return false;
  let node: Element | null = target;
  while (node && drawer.contains(node)) {
    if (node instanceof HTMLElement) {
      const overflowY = getComputedStyle(node).overflowY;
      const scrollable =
        (overflowY === "auto" || overflowY === "scroll") &&
        node.scrollHeight > node.clientHeight;
      if (scrollable) {
        const canMoveUp = deltaY < 0 && node.scrollTop > 0;
        const canMoveDown =
          deltaY > 0 &&
          node.scrollTop + node.clientHeight < node.scrollHeight - 1;
        if (canMoveUp || canMoveDown) return true;
      }
    }
    node = node.parentElement;
  }
  return false;
}

function preventDrawerScrollChain(event: WheelEvent) {
  if (!canDrawerScroll(event.target, event.deltaY)) event.preventDefault();
}

function rememberDrawerTouch(event: TouchEvent) {
  lastTouchY = event.touches[0]?.clientY ?? null;
}

function preventDrawerTouchChain(event: TouchEvent) {
  const currentY = event.touches[0]?.clientY;
  if (currentY == null || lastTouchY == null) return;
  const deltaY = lastTouchY - currentY;
  lastTouchY = currentY;
  if (!canDrawerScroll(event.target, deltaY)) event.preventDefault();
}

function lockRootScroll() {
  if (rootScrollLockCount === 0) {
    rootOverflow = document.documentElement.style.overflow;
    rootOverscrollBehavior = document.documentElement.style.overscrollBehavior;
    bodyOverflow = document.body.style.overflow;
    bodyOverscrollBehavior = document.body.style.overscrollBehavior;
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    pinBody();
    document.addEventListener("wheel", preventDrawerScrollChain, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchstart", rememberDrawerTouch, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchmove", preventDrawerTouchChain, {
      capture: true,
      passive: false,
    });
  }
  rootScrollLockCount += 1;
  return () => {
    rootScrollLockCount = Math.max(0, rootScrollLockCount - 1);
    if (rootScrollLockCount === 0) {
      document.documentElement.style.overflow = rootOverflow;
      document.documentElement.style.overscrollBehavior =
        rootOverscrollBehavior;
      document.body.style.overflow = bodyOverflow;
      document.body.style.overscrollBehavior = bodyOverscrollBehavior;
      unpinBody();
      document.removeEventListener("wheel", preventDrawerScrollChain, true);
      document.removeEventListener("touchstart", rememberDrawerTouch, true);
      document.removeEventListener("touchmove", preventDrawerTouchChain, true);
      lastTouchY = null;
    }
  };
}

function Drawer({
  onOpenChange,
  open,
  defaultOpen = false,
  repositionInputs = false,
  disablePreventScroll = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isOpen = open ?? uncontrolledOpen;
  const nativeBackRef = React.useRef<() => void>(() => {});

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
      if (!nextOpen) {
        scheduleModalPointerEventsCleanup();
      }
    },
    [onOpenChange],
  );

  nativeBackRef.current = () => {
    if (props.dismissible !== false) handleOpenChange(false);
  };
  React.useEffect(() => {
    if (!isOpen || props.modal === false) return;
    return registerNativeModalBack(() => nativeBackRef.current());
  }, [isOpen, props.modal]);

  React.useEffect(() => {
    if (open === false) {
      scheduleModalPointerEventsCleanup();
    }
  }, [open]);

  React.useEffect(() => {
    if (!isOpen || props.modal === false) return;
    return lockRootScroll();
  }, [isOpen, props.modal]);

  return (
    <DrawerDismissContext.Provider value={() => handleOpenChange(false)}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        {...props}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        repositionInputs={repositionInputs}
        disablePreventScroll={disablePreventScroll}
      />
    </DrawerDismissContext.Provider>
  );
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
  className,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
  onClick,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  const dismiss = React.useContext(DrawerDismissContext);
  const pointerStartRef = React.useRef<{
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);
  const dismissOnClickRef = React.useRef(false);

  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-100 bg-black/60 backdrop-blur-md data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
      onPointerDown={(event) => {
        // Radix normally dismisses on pointerdown, which can expose a feed card
        // before the matching pointerup/click arrives. Keep the overlay alive
        // for the full gesture and dismiss it ourselves on pointerup instead.
        event.preventDefault();
        dismissOnClickRef.current = false;
        pointerStartRef.current = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
        onPointerDown?.(event);
      }}
      onPointerUp={(event) => {
        const start = pointerStartRef.current;
        pointerStartRef.current = null;
        onPointerUp?.(event);
        dismissOnClickRef.current = Boolean(
          start?.pointerId === event.pointerId &&
            Math.hypot(event.clientX - start.x, event.clientY - start.y) < 8,
        );
      }}
      onPointerCancel={(event) => {
        pointerStartRef.current = null;
        dismissOnClickRef.current = false;
        onPointerCancel?.(event);
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
        if (dismissOnClickRef.current) {
          dismissOnClickRef.current = false;
          dismiss?.();
        }
      }}
    />
  );
}

/** `max-w-*` or an arbitrary `w-[…]`, at any breakpoint. */
const OWN_WIDTH_CLASS = /(?:^|\s|:)(?:max-w-|w-\[)/;

function DrawerContent({
  className,
  handleClassName,
  overlayClassName,
  layer,
  showHandle = true,
  style,
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  handleClassName?: string;
  overlayClassName?: string;
  /** Base z-index for this drawer's overlay; content renders one layer above. */
  layer?: number;
  showHandle?: boolean;
}) {
  const describedByProps =
    "aria-describedby" in props
      ? {}
      : ({ "aria-describedby": undefined } as const);
  // A drawer that sizes itself keeps its own width; the rest stop spanning a
  // desktop (or iPhone Duo's inner display, where iOS centres sheets too).
  const sizesItself = OWN_WIDTH_CLASS.test(className ?? "");

  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay
        className={overlayClassName}
        style={layer === undefined ? undefined : { zIndex: layer }}
      />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        {...describedByProps}
        className={cn(
          "group/drawer-content fixed z-100 flex h-auto flex-col overscroll-contain border-border bg-surface text-text-primary shadow-[0_-15px_30px_rgba(15,23,42,0.16),inset_0_1px_1.5px_rgba(255,255,255,0.55)] backdrop-blur-xl dark:border-white/10 dark:bg-black/90 dark:shadow-[0_-15px_30px_rgba(0,0,0,0.5),inset_0_1px_1.5px_rgba(255,255,255,0.15)]",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:rounded-b-2xl data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:rounded-t-2xl data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          "data-[state=open]:!animate-none",
          !sizesItself &&
            "data-[vaul-drawer-direction=bottom]:md:mx-auto data-[vaul-drawer-direction=bottom]:md:max-w-2xl data-[vaul-drawer-direction=bottom]:md:border-x",
          className,
        )}
        style={{
          ...style,
          ...(layer === undefined ? {} : { zIndex: layer + 1 }),
        }}
        {...props}
      >
        {showHandle ? (
          <div
            aria-hidden="true"
            data-slot="drawer-handle"
            className={cn(
              "mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-full bg-border/80 dark:bg-white/20",
              "group-data-[vaul-drawer-direction=left]/drawer-content:hidden group-data-[vaul-drawer-direction=right]/drawer-content:hidden group-data-[vaul-drawer-direction=top]/drawer-content:hidden",
              handleClassName,
            )}
          />
        ) : null}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
